import type {
  CreateIntegrationAccessRequest,
  CreateIntegrationRequest,
  IntegrationAccessDraft,
  IntegrationAccessResult,
  IntegrationList,
  IntegrationRequestLog,
  IntegrationView,
  IssueApiKeyRequest,
  IssuedApiKey,
  NarrowIntegrationAccessRequest,
  ProcessingInstructionView,
  RevokeIntegrationAccessRequest,
} from "@planerius/contracts/public-api"
import type {
  CreateWebhookEndpointRequest,
  TestWebhookEndpointResponse,
  WebhookDeliveryList,
  WebhookEndpointCreated,
  WebhookEndpointList,
  WebhookEndpointView,
} from "@planerius/contracts/public-api-webhooks"

import { apiRequest } from "./http"

/**
 * «Разработчикам» (4-В6/В7/В8): своя интеграция салона, доступ с поручением, ключ,
 * уведомления о событиях и журнал обращений. Право раздела — api_keys.manage, адресов
 * вебхуков — webhooks.manage; данные клиентов открывает только владелец со вторым фактором.
 */

export function fetchIntegrations(signal?: AbortSignal): Promise<IntegrationList> {
  return apiRequest("/cabinet/integrations", { signal })
}

export function createIntegration(request: CreateIntegrationRequest): Promise<IntegrationView> {
  return apiRequest("/cabinet/integrations", { method: "POST", body: request })
}

/** Текст поручения по черновику доступа — ровно тот, что будет принят. Ничего не меняет. */
export function fetchInstruction(id: string, draft: IntegrationAccessDraft, signal?: AbortSignal): Promise<ProcessingInstructionView> {
  return apiRequest(`/cabinet/integrations/${id}/instruction`, { method: "POST", body: draft, signal })
}

/** «Акцептовать и выпустить ключ»: доступ, поручение и первый ключ одним действием. */
export function grantAccess(id: string, request: CreateIntegrationAccessRequest): Promise<IntegrationAccessResult> {
  return apiRequest(`/cabinet/integrations/${id}/access`, { method: "POST", body: request })
}

export function narrowAccess(id: string, request: NarrowIntegrationAccessRequest): Promise<IntegrationView> {
  return apiRequest(`/cabinet/integrations/${id}/access/narrow`, { method: "POST", body: request })
}

export function revokeAccess(id: string, request: RevokeIntegrationAccessRequest): Promise<IntegrationView> {
  return apiRequest(`/cabinet/integrations/${id}/access/revoke`, { method: "POST", body: request })
}

export function fetchRequestLog(
  id: string,
  params: { readonly only: "all" | "errors"; readonly cursor: string | null },
  signal?: AbortSignal,
): Promise<IntegrationRequestLog> {
  const query = new URLSearchParams({ only: params.only, limit: "50" })
  if (params.cursor !== null) query.set("cursor", params.cursor)
  return apiRequest(`/cabinet/integrations/${id}/requests?${query.toString()}`, { signal })
}

/** «Выпустить новый» ключ того же доступа — когда первый ушёл не скопированным. */
export function issueKey(request: IssueApiKeyRequest): Promise<IssuedApiKey> {
  return apiRequest("/cabinet/api-keys", { method: "POST", body: request })
}

/** Отзыв ключа по kid — «Отозвать этот ключ» в состоянии «ушёл, не скопировав». */
export function revokeKey(kid: string): Promise<{ readonly revoked: true }> {
  return apiRequest(`/cabinet/api-keys/${kid}/revoke`, { method: "POST", body: { reason: "leaked" } })
}

// ───────────────────────── уведомления о событиях (4-В8) ─────────────────────────

export function fetchWebhooks(id: string, signal?: AbortSignal): Promise<WebhookEndpointList> {
  return apiRequest(`/cabinet/integrations/${id}/webhooks`, { signal })
}

export function createWebhook(id: string, request: CreateWebhookEndpointRequest): Promise<WebhookEndpointCreated> {
  return apiRequest(`/cabinet/integrations/${id}/webhooks`, { method: "POST", body: request })
}

export function testWebhook(
  id: string,
  endpointId: string,
): Promise<{ readonly endpoint: WebhookEndpointView; readonly test: TestWebhookEndpointResponse }> {
  return apiRequest(`/cabinet/integrations/${id}/webhooks/${endpointId}/test`, { method: "POST", body: {} })
}

export function setWebhookState(
  id: string,
  endpointId: string,
  action: "pause" | "resume" | "disable",
): Promise<WebhookEndpointView> {
  return apiRequest(`/cabinet/integrations/${id}/webhooks/${endpointId}/${action}`, { method: "POST", body: {} })
}

export function fetchDeliveries(id: string, endpointId: string, signal?: AbortSignal): Promise<WebhookDeliveryList> {
  return apiRequest(`/cabinet/integrations/${id}/webhooks/${endpointId}/deliveries?limit=20`, { signal })
}

export function retryFailedDeliveries(id: string, endpointId: string): Promise<{ readonly requeued: number }> {
  return apiRequest(`/cabinet/integrations/${id}/webhooks/${endpointId}/deliveries/retry-failed`, {
    method: "POST",
    body: {},
  })
}
